import type { Node } from '@xyflow/react';

export type EditorAsset = {
  type: 'image' | 'video' | 'audio';
  url: string;
  filename: string;
  width?: number;
  height?: number;
  durationInSeconds?: number;
  hasAudioTrack?: boolean;
};

export function extractFilename(url: string): string {
  try {
    const u = new URL(url);
    return u.pathname.split('/').pop() || 'asset';
  } catch {
    return 'asset';
  }
}

export function buildAssetsFromNodes(nodes: Node[]): EditorAsset[] {
  const collected: EditorAsset[] = [];
  const seen = new Set<string>();

  for (const n of nodes) {
    const d: any = n.data || {};

    const pickUrl = (...candidates: Array<string | undefined>): string | undefined => {
      for (const u of candidates) if (typeof u === 'string' && u) return u;
      return undefined;
    };

    let url: string | undefined;
    let type: EditorAsset['type'] | undefined;

    if (n.type === 'image') {
      url = pickUrl(d?.generated?.url, d?.content?.url, d?.contentUrl, d?.url);
      type = 'image';
    } else if (n.type === 'video') {
      url = pickUrl(d?.content?.url, d?.generated?.url, d?.contentUrl, d?.url);
      type = 'video';
    } else if (n.type === 'audio') {
      url = pickUrl(d?.content?.url, d?.generated?.url, d?.contentUrl, d?.url);
      type = 'audio';
    }

    if (url && type) {
      if (seen.has(url)) continue;
      seen.add(url);
      collected.push({ type, url, filename: extractFilename(url) });
    }
  }

  return collected;
}

export function toBase64Url(input: string): string {
  return btoa(String.fromCharCode(...new TextEncoder().encode(input)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export function makeEditorHref(base: string, nodes: Node[]): string {
  const assets = buildAssetsFromNodes(nodes);
  const json = JSON.stringify(assets);
  return `${base}#assets=${toBase64Url(json)}`;
}

function withTimeout<T>(p: Promise<T>, ms = 5000): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('metadata-timeout')), ms);
    p.then((v) => { clearTimeout(t); resolve(v); }).catch((e) => { clearTimeout(t); reject(e); });
  });
}

export async function readImageMeta(url: string): Promise<Pick<EditorAsset, 'width'|'height'>> {
  return withTimeout(new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve({ width: img.naturalWidth || undefined as any, height: img.naturalHeight || undefined as any });
    img.onerror = () => resolve({} as any);
    img.src = url;
  }));
}

export async function readAudioMeta(url: string): Promise<Pick<EditorAsset, 'durationInSeconds'>> {
  return withTimeout(new Promise((resolve) => {
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    audio.crossOrigin = 'anonymous';
    const done = () => {
      const duration = Number.isFinite(audio.duration) ? audio.duration : undefined;
      resolve({ durationInSeconds: duration } as any);
      cleanup();
    };
    const cleanup = () => {
      audio.src = '';
      audio.remove();
    };
    audio.addEventListener('loadedmetadata', done, { once: true });
    audio.addEventListener('error', () => { resolve({} as any); cleanup(); }, { once: true });
    audio.src = url;
  }));
}

function detectVideoHasAudio(video: HTMLVideoElement): boolean | undefined {
  try {
    if (typeof (video as any).mozHasAudio === 'boolean') return (video as any).mozHasAudio;
    // webkitAudioDecodedByteCount is non-standard but works on some browsers
    if (typeof (video as any).webkitAudioDecodedByteCount === 'number') return (video as any).webkitAudioDecodedByteCount > 0;
    const tracks = (video as any).audioTracks; // may be undefined in many cases
    if (tracks && typeof tracks.length === 'number') return tracks.length > 0;
  } catch {}
  return undefined;
}

export async function readVideoMeta(url: string): Promise<Pick<EditorAsset, 'width'|'height'|'durationInSeconds'|'hasAudioTrack'>> {
  return withTimeout(new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.crossOrigin = 'anonymous';
    const done = () => {
      const width = (video.videoWidth && Number.isFinite(video.videoWidth)) ? video.videoWidth : undefined;
      const height = (video.videoHeight && Number.isFinite(video.videoHeight)) ? video.videoHeight : undefined;
      const duration = Number.isFinite(video.duration) ? video.duration : undefined;
      const hasAudioTrack = detectVideoHasAudio(video);
      resolve({ width, height, durationInSeconds: duration, hasAudioTrack } as any);
      cleanup();
    };
    const cleanup = () => {
      video.src = '';
      video.remove();
    };
    video.addEventListener('loadedmetadata', done, { once: true });
    video.addEventListener('error', () => { resolve({} as any); cleanup(); }, { once: true });
    video.src = url;
  }));
}

export async function buildAssetsFromNodesWithMeta(nodes: Node[]): Promise<EditorAsset[]> {
  const base = buildAssetsFromNodes(nodes);
  const enriched = await Promise.all(base.map(async (a) => {
    try {
      if (a.type === 'image') {
        const meta = await readImageMeta(a.url);
        return { ...a, ...meta };
      }
      if (a.type === 'video') {
        const meta = await readVideoMeta(a.url);
        return { ...a, ...meta };
      }
      if (a.type === 'audio') {
        const meta = await readAudioMeta(a.url);
        return { ...a, ...meta };
      }
    } catch {}
    return a;
  }));
  return enriched;
}

export async function makeEditorHrefAsync(base: string, nodes: Node[]): Promise<string> {
  const assets = await buildAssetsFromNodesWithMeta(nodes);
  const json = JSON.stringify(assets);
  return `${base}#assets=${toBase64Url(json)}`;
}


